// ─────────────────────────────────────────────────────────────
// AppHeader — top tab bar for the Maps Discover Studio app.
// Left: logo + name. Center: tab buttons. Right: controls.
// Displays the signed-in user's name and a sign-out button.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { MapsLogo } from "../../components/MapsLogo";
import { extractFirstName, extractLastName } from "./AiPanel";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router-dom";
import type { PaletteMode } from "@mui/material";
import { version as packageVersion } from '../../../package.json';
import { healthApi } from "../../api/client";
import type { AuthUser } from "../../api/client";
import type { ScriptHelperTab } from "./types";

const TABS: { id: ScriptHelperTab; label: string }[] = [
  { id: "welcome", label: "Welcome" },
  { id: "python", label: "Editor" },
  { id: "output", label: "Output" },
  { id: "imageLibrary", label: "Image Library" },
  { id: "scripts", label: "Scripts" },
  { id: "community", label: "Community" },
  { id: "help", label: "Help" },
];

// Preview-build override target for the Dashboard nav link.
//
// The preview stack ships a copy of Discover Studio for stakeholder
// feedback. Historically we always funneled the Dashboard link from
// preview into the single production dashboard database so that all
// feature requests / bug reports / backlog ideas landed in one place.
//
// That funneling is now opt-in via the build-time flag
// `VITE_PREVIEW_FUNNEL_DASHBOARD_TO_PROD`. When the flag is set to
// `"true"` at build time (e.g. a Docker `--build-arg` on the preview
// image), the Dashboard icon in a preview build renders as an `<a>`
// pointing at the production dashboard URL below. When the flag is
// unset or any other value, the preview build behaves the same as
// local dev and production — `navigate("/dashboard")` to the in-app
// route. Default is OFF so preview stands on its own (useful for
// recording walkthroughs / demos against the preview dashboard).
//
// To re-enable funneling for a preview deploy, set the flag at build
// time alongside the other VITE_* args:
//   --build-arg VITE_PREVIEW_FUNNEL_DASHBOARD_TO_PROD=true
const PROD_DASHBOARD_URL = "https://maps.thermofisher.com/discoverstudio/dashboard";

// Build-time flag: should a preview build funnel the Dashboard link
// to production? Defaults to false (preview keeps its own dashboard).
const PREVIEW_FUNNEL_DASHBOARD_TO_PROD =
  String(import.meta.env.VITE_PREVIEW_FUNNEL_DASHBOARD_TO_PROD ?? "")
    .toLowerCase() === "true";

interface AppHeaderProps {
  activeTab: ScriptHelperTab;
  onTabChange: (tab: ScriptHelperTab) => void;
  user: AuthUser;
  onSignOut: () => void;
  onSettingsClick: () => void;
  colorMode: PaletteMode;
  onToggleColorMode: () => void;
}

export function AppHeader({
  activeTab,
  onTabChange,
  user,
  onSignOut,
  onSettingsClick,
  colorMode,
  onToggleColorMode,
}: AppHeaderProps) {
  const tabIndex = TABS.findIndex((t) => t.id === activeTab);
  const navigate = useNavigate();

  const displayName = user.full_name?.trim() || user.email;

  // ── User menu state ──────────────────────────────────────
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Live version + build counter from /api/health — single source of truth
  // is the VERSION file at repo root (auto-bumped per commit by the
  // pre-commit hook). Falls back to the static package.json version on
  // fetch failure so the menu never shows blank.
  const [versionLabel, setVersionLabel] = useState<string>(`v${packageVersion}`);
  useEffect(() => {
    healthApi.get()
      .then((h) => setVersionLabel(`v${h.version}+${h.build}`))
      .catch(() => { /* keep package.json fallback */ });
  }, []);
  const menuOpen = Boolean(menuAnchor);

  function handleUserButtonClick(event: React.MouseEvent<HTMLElement>) {
    setMenuAnchor(event.currentTarget);
  }

  function handleMenuClose() {
    setMenuAnchor(null);
  }

  function handleSettingsMenuItem() {
    handleMenuClose();
    onSettingsClick();
  }

  function handleAdminMenuItem() {
    handleMenuClose();
    navigate("/admin");
  }

  function handleSignOutMenuItem() {
    handleMenuClose();
    onSignOut();
  }

  // Compute avatar initials. Uses the comma-aware helpers shared with the
  // Marie chat avatar so the corporate TF SSO "Lastname, Firstname [M]"
  // format produces "GC" for "Clark, Greg J" (not "CJ"). Single-name and
  // "Firstname Lastname" inputs continue to work correctly.
  function getInitials(): string {
    const name = user.full_name?.trim();
    if (name) {
      const first = extractFirstName(name);
      const last = extractLastName(name);
      if (first && last) return (first[0] + last[0]).toUpperCase();
      if (first) return first[0].toUpperCase();
    }
    return user.email[0].toUpperCase();
  }

  function handleTabChange(_: React.SyntheticEvent, newIndex: number) {
    onTabChange(TABS[newIndex].id);
  }

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{ zIndex: 10, flexShrink: 0 }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          px: 1.5,
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* ── Logo + Name ─────────────────────────────────────── */}
        <Box
          component="button"
          onClick={() => onTabChange("welcome")}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            background: "none",
            border: "none",
            cursor: "pointer",
            p: 0,
            mr: 1,
            flexShrink: 0,
            borderRadius: 1,
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: "2px",
            },
          }}
          aria-label="Go to Welcome tab"
        >
          <MapsLogo size="medium" />
          <Typography
            variant="subtitle2"
            color="text.primary"
            sx={{ whiteSpace: "nowrap", userSelect: "none" }}
          >
            Discover Studio
          </Typography>
        </Box>

        {/* ── Tabs ────────────────────────────────────────────── */}
        <Tabs
          value={tabIndex === -1 ? false : tabIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{
            flex: 1,
            minHeight: 48,
            "& .MuiTabs-indicator": { height: 2 },
            "& .MuiTab-root": {
              minHeight: 48,
              py: 0,
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.id} label={tab.label} disableRipple />
          ))}
        </Tabs>

        {/* ── Right Controls ──────────────────────────────────── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {/*
            Preview environment override (opt-in): when this is a
            preview build (BASE_URL === "/scripthelper-preview/") AND
            the build-time flag VITE_PREVIEW_FUNNEL_DASHBOARD_TO_PROD
            is "true", the Dashboard link funnels into the single
            production dashboard so all feature requests / bug reports
            land in one database. Default (flag unset/false) — and
            prod build and local dev — keep the in-app navigate.
            See PREVIEW_FUNNEL_DASHBOARD_TO_PROD definition above.
          */}
          {(import.meta.env.BASE_URL ?? "").startsWith("/scripthelper-preview") &&
            PREVIEW_FUNNEL_DASHBOARD_TO_PROD ? (
            <Tooltip title="Go to Dashboard">
              <IconButton
                size="small"
                component="a"
                href={PROD_DASHBOARD_URL}
                aria-label="Go to Dashboard"
              >
                <DashboardOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Go to Dashboard">
              <IconButton
                size="small"
                onClick={() => navigate("/dashboard")}
                aria-label="Go to Dashboard"
              >
                <DashboardOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
            <IconButton
              size="small"
              onClick={onToggleColorMode}
              aria-label={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {colorMode === "dark" ? (
                <LightModeOutlinedIcon fontSize="small" />
              ) : (
                <DarkModeOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* ── User account dropdown ─────────────────────────── */}
          <Button
            size="small"
            onClick={handleUserButtonClick}
            aria-controls={menuOpen ? "user-account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            aria-label="User account menu"
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "16px !important" }} />}
            sx={{
              textTransform: "none",
              px: 0.75,
              py: 0.25,
              minWidth: 0,
              gap: 0.75,
              color: "text.primary",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: "0.625rem",
                fontWeight: 600,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              {getInitials()}
            </Avatar>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{
                maxWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
          </Button>

          <Menu
            id="user-account-menu"
            anchorEl={menuAnchor}
            open={menuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: { sx: { minWidth: 220, mt: 0.5 } },
            }}
          >
            {/* Non-clickable identity header */}
            <Box sx={{ px: 2, py: 1, pointerEvents: "none" }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user.full_name?.trim() || user.email}
              </Typography>
              {user.full_name?.trim() && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {user.email}
                </Typography>
              )}
            </Box>

            <Divider />

            <MenuItem onClick={handleSettingsMenuItem}>
              <ListItemIcon>
                <ManageAccountsOutlinedIcon fontSize="small" />
              </ListItemIcon>
              User Settings
            </MenuItem>

            {user.is_admin && (
              <MenuItem onClick={handleAdminMenuItem}>
                <ListItemIcon>
                  <AdminPanelSettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Admin
              </MenuItem>
            )}

            <MenuItem onClick={handleSignOutMenuItem}>
              <ListItemIcon>
                <LogoutOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Sign Out
            </MenuItem>

            <Divider />

            <MenuItem disabled sx={{ py: 0.5 }}>
              <Typography variant="caption" color="text.disabled">
                {versionLabel}
              </Typography>
            </MenuItem>
          </Menu>

        </Box>
      </Toolbar>
    </AppBar>
  );
}
